import { type ActionRun, type CreateActionRunOptions } from '@lucidtech/las-sdk-browser';

import { create } from '@/hooks/api';
import { isActionRunId, JSONType } from '@/utils';

export type ActionRunId = { actionId: string; runId: string };

export const {
  getOptions: getActionRunOptions,
  useBatchGet: useBatchGetActionRun,
  useCreate: useCreateActionRun,
  useDelete: useDeleteActionRun,
  useGet: useGetActionRun,
  useInvalidateQueries: useInvalidateActionRunQueries,
  useUpdate: useUpdateActionRun,
} = create<ActionRun, ActionRunId, CreateActionRunOptions & { actionId: string }>({
  cacheKey: 'actionRuns',
  equal: (actionRun, id) =>
    Boolean(
      actionRun && id?.actionId && id?.runId && actionRun.actionId === id.actionId && actionRun.runId === id.runId
    ),
  createId: (actionRun) => (actionRun ? { actionId: actionRun.actionId, runId: actionRun.runId } : null),
  create: (client, { actionId, ...rest }) => client.createActionRun(actionId, { ...rest }),
  get: (client, id) => client.getActionRun(id.actionId, id.runId),
  delete: (client, id) => client.deleteActionRun(id.actionId, id.runId),
  list: (client, options) => {
    return new Promise((resolve, reject) => {
      client
        .listActionRuns(options)
        .then((response: JSONType) => {
          resolve({ data: response.runs, nextToken: response.nextToken });
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  update: (client, id, updates) => client.updateActionRun(id.actionId, id.runId, updates),
});

export function toActionRunId(actionId?: string | null, runId?: string | null): ActionRunId | null;
export function toActionRunId(runId?: string | null): ActionRunId | null;

export function toActionRunId(actionIdOrRunId?: string | null, runId?: string | null): ActionRunId | null {
  let _actionId = runId ? actionIdOrRunId : undefined;
  let _runId = runId ?? actionIdOrRunId;

  if (isActionRunId(_runId)) {
    [_actionId, _runId] = _runId.split('/');
  }

  return _actionId && _runId ? { actionId: _actionId, runId: _runId } : null;
}
